<template>
  <v-app>
    <div class="auth-root">
      <!-- form panel (comes first: right side in RTL, left side in LTR) -->
      <main class="auth-main">
        <div class="auth-topbar">
          <img src="@/assets/login/abrIronLogo.png" alt="Abriron" class="auth-logo" />
          <AppHeader />
        </div>

        <!-- mobile hero -->
        <div class="auth-hero">
          <transition name="hero-fade" mode="out-in">
            <div :key="active" class="hero-text">
              <v-icon small color="white">{{ slides[active].icon }}</v-icon>
              <span>{{ $t(slides[active].title) }}</span>
            </div>
          </transition>
        </div>

        <div class="auth-body">
          <div class="auth-card">
            <transition name="auth-fade" mode="out-in">
              <LoginForm
                v-if="!signUpStep && !businessSelector"
                key="login"
                :signUpSection="signUpStep"
                @switchMode="signUpStep = $event"
                @business-selector="handleBusinessSelector"
              />
              <RegisterForm
                v-else-if="signUpStep && !businessSelector"
                key="register"
                :signUpSection="signUpStep"
                @switchMode="signUpStep = $event"
                @business-selector="handleBusinessSelector"
              />
              <BusinessSelector
                v-else
                key="business"
                :userData="userBusinessData"
                :authToken="token"
                @exit="businessSelector = false"
              />
            </transition>
          </div>
        </div>

        <AppFooter />
      </main>

      <!-- brand slider (desktop) -->
      <aside class="auth-brand" @mouseenter="pause" @mouseleave="play">
        <div class="brand-glow brand-glow--1"></div>
        <div class="brand-glow brand-glow--2"></div>
        <div class="brand-grid"></div>

        <div class="brand-inner">
          <!-- visual -->
          <div class="slide-stage">
            <transition name="visual" mode="out-in">
              <div :key="active" class="visual" :class="'visual--' + slides[active].id">
                <!-- AI assistant -->
                <div v-if="slides[active].id === 'ai'" class="glass mock-ai">
                  <div class="ai-head">
                    <span class="ai-orb"><v-icon small color="white">mdi-creation</v-icon></span>
                    <span class="ai-name">{{ $t('auth ai name') }}</span>
                    <span class="ai-live"><i></i>{{ $t('auth ai online') }}</span>
                  </div>
                  <div class="ai-msg ai-msg--user">{{ $t('auth ai prompt') }}</div>
                  <div class="ai-typing"><i></i><i></i><i></i></div>
                  <div class="ai-msg ai-msg--bot">{{ $t('auth ai reply') }}</div>
                  <div class="ai-tasks">
                    <div v-for="(t, i) in aiTasks" :key="t.key" class="ai-task" :style="{ animationDelay: 2.1 + i * 0.45 + 's' }">
                      <span class="ai-task-ic" :style="{ background: t.color }"><v-icon x-small color="white">{{ t.icon }}</v-icon></span>
                      <span class="ai-task-txt">{{ $t(t.key) }}</span>
                      <span class="ai-check" :style="{ animationDelay: 2.4 + i * 0.45 + 's' }"><v-icon x-small color="white">mdi-check</v-icon></span>
                    </div>
                  </div>
                  <div class="float-chip float-chip--ai">
                    <v-icon small color="#a78bfa">mdi-lightning-bolt</v-icon><b>{{ $t('auth ai chip') }}</b>
                  </div>
                </div>

                <!-- reports -->
                <div v-else-if="slides[active].id === 'reports'" class="glass mock-chart">
                  <div class="kpis">
                    <div v-for="(k, i) in kpis" :key="i" class="kpi" :style="{ animationDelay: i * 0.1 + 's' }">
                      <span class="kpi-dot" :style="{ background: k.color }"></span>
                      <b>{{ k.value }}</b>
                      <small :class="k.up ? 'up' : 'down'">
                        <v-icon x-small :color="k.up ? '#34d399' : '#fb7185'">{{ k.up ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>{{ k.delta }}
                      </small>
                    </div>
                  </div>
                  <div class="chart-box">
                    <svg viewBox="0 0 300 120" preserveAspectRatio="none" class="line-chart">
                      <defs>
                        <linearGradient id="authArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#22d3ee" stop-opacity=".45" />
                          <stop offset="100%" stop-color="#22d3ee" stop-opacity="0" />
                        </linearGradient>
                        <linearGradient id="authLine" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stop-color="#60a5fa" />
                          <stop offset="100%" stop-color="#22d3ee" />
                        </linearGradient>
                      </defs>
                      <g class="grid-lines">
                        <line x1="0" y1="30" x2="300" y2="30" />
                        <line x1="0" y1="60" x2="300" y2="60" />
                        <line x1="0" y1="90" x2="300" y2="90" />
                      </g>
                      <path class="area" d="M0,96 C30,88 50,92 75,74 S120,70 150,56 S205,52 230,34 S275,24 300,14 L300,120 L0,120 Z" fill="url(#authArea)" />
                      <path class="line line--2" d="M0,104 C40,100 60,98 90,92 S150,86 180,80 S250,70 300,62" />
                      <path class="line" d="M0,96 C30,88 50,92 75,74 S120,70 150,56 S205,52 230,34 S275,24 300,14" stroke="url(#authLine)" />
                      <circle class="end-dot" cx="300" cy="14" r="4" />
                    </svg>
                    <div class="bars">
                      <span v-for="(h, i) in [30, 46, 38, 58, 50, 72, 64, 86]" :key="i" :style="{ height: h + '%', animationDelay: 0.4 + i * 0.06 + 's' }"></span>
                    </div>
                  </div>
                  <div class="float-chip float-chip--a">
                    <v-icon small color="#34d399">mdi-trending-up</v-icon><b>+24%</b>
                  </div>
                  <div class="mini-card mini-card--donut">
                    <svg viewBox="0 0 36 36" class="donut">
                      <circle cx="18" cy="18" r="14" class="donut-bg" />
                      <circle cx="18" cy="18" r="14" class="donut-val" />
                    </svg>
                    <b>68%</b>
                  </div>
                </div>

                <!-- invoices -->
                <div v-else-if="slides[active].id === 'invoice'" class="glass mock-invoice">
                  <div class="inv-head">
                    <span class="inv-ic"><v-icon small color="white">mdi-receipt-text-outline</v-icon></span>
                    <span class="lines"><i></i><i class="short"></i></span>
                    <span class="inv-no">#1042</span>
                  </div>
                  <div v-for="i in 3" :key="i" class="inv-row" :style="{ animationDelay: 0.15 + i * 0.12 + 's' }">
                    <span class="lines"><i :style="{ width: [70, 55, 80][i - 1] + '%' }"></i></span>
                    <span class="qty"></span>
                    <span class="amt"></span>
                  </div>
                  <div class="inv-total">
                    <span class="lines"><i class="short"></i></span>
                    <b>۴۸,۷۵۰,۰۰۰</b>
                  </div>
                  <div class="stamp"><v-icon small color="#34d399">mdi-check-decagram</v-icon>{{ $t('auth invoice paid') }}</div>
                  <div class="toast">
                    <span class="toast-ic"><v-icon x-small color="white">mdi-book-check-outline</v-icon></span>
                    <span class="lines"><i></i><i class="short"></i></span>
                  </div>
                </div>

                <!-- inventory -->
                <div v-else-if="slides[active].id === 'stock'" class="glass mock-stock">
                  <div v-for="(item, i) in stock" :key="i" class="stock-row" :style="{ animationDelay: i * 0.1 + 's' }">
                    <span class="stock-ic" :style="{ background: item.color }"><v-icon small color="white">{{ item.icon }}</v-icon></span>
                    <span class="stock-body">
                      <span class="lines"><i :style="{ width: item.w + '%' }"></i></span>
                      <span class="meter"><span :style="{ width: item.level + '%', background: item.level < 25 ? '#fb7185' : item.color, animationDelay: 0.3 + i * 0.1 + 's' }"></span></span>
                    </span>
                    <b class="stock-val">{{ item.level }}%</b>
                  </div>
                  <div class="float-chip float-chip--warn">
                    <v-icon small color="#fbbf24">mdi-alert-outline</v-icon><b>{{ $t('auth stock low') }}</b>
                  </div>
                  <div class="mini-card mini-card--scan">
                    <v-icon color="white">mdi-barcode-scan</v-icon>
                    <span class="scan-line"></span>
                  </div>
                </div>

                <!-- security -->
                <div v-else class="glass mock-shield">
                  <div class="ring ring--1"></div>
                  <div class="ring ring--2"></div>
                  <div class="ring ring--3"></div>
                  <div class="orbit"><span class="sat"><v-icon x-small color="white">mdi-cloud-check-outline</v-icon></span></div>
                  <div class="orbit orbit--2"><span class="sat sat--2"><v-icon x-small color="white">mdi-lock-outline</v-icon></span></div>
                  <div class="shield"><v-icon size="42" color="white">mdi-shield-check</v-icon></div>
                </div>
              </div>
            </transition>
          </div>

          <!-- copy -->
          <div class="slide-copy">
            <transition name="copy" mode="out-in">
              <div :key="active">
                <span class="slide-kicker">
                  <v-icon x-small color="white">{{ slides[active].icon }}</v-icon>
                  {{ $t(slides[active].kicker) }}
                </span>
                <h1 class="brand-title" :class="{ 'brand-title--ai': slides[active].id === 'ai' }">{{ $t(slides[active].title) }}</h1>
                <p class="brand-subtitle">{{ $t(slides[active].text) }}</p>
              </div>
            </transition>

            <div class="slide-nav">
              <button
                v-for="(s, i) in slides"
                :key="s.title"
                type="button"
                class="dot"
                :class="{ 'dot--active': i === active, 'dot--paused': paused }"
                :aria-label="$t(s.title)"
                @click="go(i)"
              >
                <span v-if="i === active" :key="cycle" class="dot-fill" :style="{ animationDuration: interval + 'ms' }"></span>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </v-app>
</template>

<script>
import LoginForm from '@/views/dashboard/pages/login-register/partial/LoginForm.vue'
import RegisterForm from '@/views/dashboard/pages/login-register/partial/RegisterForm.vue'
import BusinessSelector from '@/views/dashboard/pages/login-register/partial/BusinessSelector.vue'
import AppHeader from '@/views/dashboard/pages/login-register/partial/AppHeader.vue'
import AppFooter from '@/views/dashboard/pages/login-register/partial/Footer.vue'

export default {
  name: 'Login',
  components: {
    LoginForm,
    RegisterForm,
    BusinessSelector,
    AppHeader,
    AppFooter
  },
  data: () => ({
    signUpStep: false,
    businessSelector: false,
    userBusinessData: [],
    token: null,
    active: 0,
    cycle: 0,
    paused: false,
    interval: 6000,
    timerId: null,
    slides: [
      { id: 'ai', icon: 'mdi-creation', kicker: 'auth slide5 kicker', title: 'auth slide5 title', text: 'auth slide5 text' },
      { id: 'reports', icon: 'mdi-chart-box-outline', kicker: 'auth slide1 kicker', title: 'auth slide1 title', text: 'auth slide1 text' },
      { id: 'invoice', icon: 'mdi-receipt-text-outline', kicker: 'auth slide2 kicker', title: 'auth slide2 title', text: 'auth slide2 text' },
      { id: 'stock', icon: 'mdi-package-variant-closed', kicker: 'auth slide3 kicker', title: 'auth slide3 title', text: 'auth slide3 text' },
      { id: 'security', icon: 'mdi-shield-check-outline', kicker: 'auth slide4 kicker', title: 'auth slide4 title', text: 'auth slide4 text' }
    ],
    aiTasks: [
      { key: 'auth ai task1', icon: 'mdi-receipt-text-check-outline', color: '#3b82f6' },
      { key: 'auth ai task2', icon: 'mdi-book-open-variant', color: '#8b5cf6' },
      { key: 'auth ai task3', icon: 'mdi-chart-line', color: '#06b6d4' }
    ],
    kpis: [
      { value: '۸۴۲M', delta: '12%', up: true, color: '#22d3ee' },
      { value: '۱۲۶M', delta: '8%', up: true, color: '#a78bfa' },
      { value: '۳۸M', delta: '3%', up: false, color: '#fbbf24' }
    ],
    stock: [
      { icon: 'mdi-package-variant', color: '#3b82f6', level: 82, w: 70 },
      { icon: 'mdi-tag-outline', color: '#8b5cf6', level: 56, w: 55 },
      { icon: 'mdi-truck-outline', color: '#06b6d4', level: 91, w: 80 },
      { icon: 'mdi-cube-outline', color: '#f59e0b', level: 14, w: 48 }
    ]
  }),
  mounted () {
    if (this.$route.query) {
      localStorage.setItem('additional_data', JSON.stringify(this.$route.query))
    }
    this.play()
  },
  beforeDestroy () {
    clearInterval(this.timerId)
  },
  methods: {
    handleBusinessSelector (value) {
      this.businessSelector = value.hasAccount
      this.userBusinessData = value.userData
      this.token = value.Authorization
    },
    next () {
      this.active = (this.active + 1) % this.slides.length
      this.cycle++
    },
    go (i) {
      this.active = i
      this.cycle++
      this.play()
    },
    play () {
      clearInterval(this.timerId)
      this.paused = false
      this.timerId = setInterval(this.next, this.interval)
    },
    pause () {
      clearInterval(this.timerId)
      this.paused = true
    }
  }
}
</script>

<style scoped>
.auth-root {
  display: flex;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  background: #fff;
  font-family: 'IRANSans', 'Vazirmatn', 'Inter', sans-serif;
  color: #0f172a;
}

/* ================= form panel ================= */
.auth-main {
  flex: 1 1 50%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(600px 300px at 50% -10%, rgba(0, 102, 255, 0.06), transparent 70%),
    #fff;
}
.auth-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 16px 40px 0;
}
.auth-logo { height: 28px; }
.auth-hero { display: none; }
.auth-body {
  flex: 1;
  min-height: 0;
  display: flex;
  /* Top-anchored so step height changes don't bounce the card */
  align-items: flex-start;
  justify-content: center;
  padding: 28px 40px 12px;
  overflow-y: auto;
}
.auth-main ::v-deep .app-footer {
  flex: 0 0 auto;
  padding: 8px 16px 12px;
}
.auth-card {
  width: 100%;
  max-width: 420px;
}
.auth-fade-enter-active, .auth-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.auth-fade-enter, .auth-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* ================= brand slider ================= */
.auth-brand {
  position: relative;
  flex: 1 1 50%;
  max-width: 760px;
  margin: 12px;
  border-radius: 16px;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(120% 80% at 0% 0%, rgba(59, 130, 246, .55) 0%, transparent 55%),
    radial-gradient(90% 70% at 100% 100%, rgba(124, 58, 237, .45) 0%, transparent 60%),
    linear-gradient(160deg, #0a3bd6 0%, #0b2577 55%, #0a1440 100%);
}
.brand-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: .5;
  pointer-events: none;
  animation: drift 16s ease-in-out infinite alternate;
}
.brand-glow--1 { width: 420px; height: 420px; background: #22d3ee; top: -160px; inset-inline-start: -140px; opacity: .35; }
.brand-glow--2 { width: 380px; height: 380px; background: #8b5cf6; bottom: -160px; inset-inline-end: -120px; animation-delay: -8s; }
@keyframes drift { to { transform: translate(50px, 40px) scale(1.15); } }
.brand-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, .06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .06) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse at 50% 38%, #000 15%, transparent 68%);
  -webkit-mask-image: radial-gradient(ellipse at 50% 38%, #000 15%, transparent 68%);
}
.brand-inner {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 48px 56px 44px;
}

/* stage */
.slide-stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 330px;
}
.visual { width: 100%; max-width: 420px; }
.glass {
  position: relative;
  border-radius: 20px;
  padding: 20px;
  background: linear-gradient(160deg, rgba(255, 255, 255, .16), rgba(255, 255, 255, .05));
  border: 1px solid rgba(255, 255, 255, .2);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 40px 80px -30px rgba(2, 8, 40, .8), inset 0 1px 0 rgba(255, 255, 255, .25);
}
.visual-enter-active, .visual-leave-active { transition: opacity .5s ease, transform .5s cubic-bezier(.2, .8, .2, 1); }
.visual-enter { opacity: 0; transform: translateY(26px) scale(.95); }
.visual-leave-to { opacity: 0; transform: translateY(-18px) scale(.98); }

.lines { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.lines i { display: block; height: 7px; border-radius: 4px; background: rgba(255, 255, 255, .55); width: 80%; }
.lines i.short { width: 45%; background: rgba(255, 255, 255, .25); }

.float-chip {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: #fff;
  color: #0f172a;
  font-size: 12.5px;
  white-space: nowrap;
  box-shadow: 0 16px 34px rgba(2, 8, 40, .4);
  animation: chipIn .6s .8s cubic-bezier(.2, .8, .2, 1.3) both, float 3.2s 1.4s ease-in-out infinite;
}
@keyframes chipIn { from { opacity: 0; transform: translateY(10px) scale(.8); } }
@keyframes float { 50% { transform: translateY(-7px); } }
.mini-card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(15, 23, 72, .75);
  border: 1px solid rgba(255, 255, 255, .18);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 16px 34px rgba(2, 8, 40, .45);
  font-size: 13px;
  animation: chipIn .6s 1s cubic-bezier(.2, .8, .2, 1.3) both, float 4s 1.6s ease-in-out infinite reverse;
}

/* ---- AI ---- */
.mock-ai { display: flex; flex-direction: column; gap: 10px; }
.ai-head { display: flex; align-items: center; gap: 10px; padding-bottom: 12px; border-bottom: 1px solid rgba(255, 255, 255, .12); }
.ai-orb {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: conic-gradient(from 0deg, #22d3ee, #8b5cf6, #ec4899, #22d3ee);
  box-shadow: 0 0 0 4px rgba(139, 92, 246, .2), 0 8px 20px rgba(139, 92, 246, .5);
  animation: orbSpin 6s linear infinite;
}
.ai-orb .v-icon { animation: orbSpin 6s linear infinite reverse; }
@keyframes orbSpin { to { transform: rotate(360deg); } }
.ai-name { font-weight: 700; font-size: 14px; flex: 1; }
.ai-live { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; opacity: .8; }
.ai-live i { width: 7px; height: 7px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 0 rgba(52, 211, 153, .6); animation: live 1.8s infinite; }
@keyframes live { 70% { box-shadow: 0 0 0 7px rgba(52, 211, 153, 0); } 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); } }
.ai-msg {
  max-width: 86%;
  padding: 9px 12px;
  font-size: 12.5px;
  line-height: 1.8;
  border-radius: 14px;
  animation: msgIn .45s cubic-bezier(.2, .8, .2, 1) both;
}
@keyframes msgIn { from { opacity: 0; transform: translateY(8px); } }
.ai-msg--user { align-self: flex-start; background: #fff; color: #0f172a; border-start-start-radius: 4px; animation-delay: .25s; }
.ai-msg--bot {
  align-self: flex-end;
  background: linear-gradient(135deg, rgba(139, 92, 246, .55), rgba(59, 130, 246, .45));
  border: 1px solid rgba(255, 255, 255, .2);
  border-start-end-radius: 4px;
  animation-delay: 1.75s;
}
.ai-typing {
  align-self: flex-end;
  display: flex;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, .14);
  overflow: hidden;
  animation: typingLife 1.8s .5s both;
}
.ai-typing i { width: 6px; height: 6px; border-radius: 50%; background: #fff; animation: dot 1s infinite; }
.ai-typing i:nth-child(2) { animation-delay: .15s; }
.ai-typing i:nth-child(3) { animation-delay: .3s; }
@keyframes dot { 0%, 100% { opacity: .3; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-3px); } }
@keyframes typingLife {
  0% { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
  10%, 70% { opacity: 1; max-height: 30px; padding-top: 10px; padding-bottom: 10px; }
  100% { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; margin-top: -10px; }
}
.ai-tasks { display: flex; flex-direction: column; gap: 6px; }
.ai-task {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .08);
  font-size: 12px;
  animation: msgIn .45s cubic-bezier(.2, .8, .2, 1) both;
}
.ai-task-ic { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; flex: 0 0 auto; }
.ai-task-txt { flex: 1; opacity: .92; }
.ai-check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #10b981;
  animation: pop .4s cubic-bezier(.2, .8, .2, 1.6) both;
}
.float-chip--ai { top: -16px; inset-inline-end: -12px; animation-delay: 3.4s, 4s; }

/* ---- reports ---- */
.kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
.kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .08);
  animation: msgIn .5s cubic-bezier(.2, .8, .2, 1) both;
}
.kpi-dot { width: 18px; height: 4px; border-radius: 2px; margin-bottom: 4px; }
.kpi b { font-size: 16px; letter-spacing: .3px; }
.kpi small { display: inline-flex; align-items: center; gap: 2px; font-size: 11px; }
.kpi small.up { color: #6ee7b7; }
.kpi small.down { color: #fda4af; }
.chart-box { position: relative; height: 150px; }
.line-chart { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; z-index: 1; }
.grid-lines line { stroke: rgba(255, 255, 255, .1); stroke-dasharray: 3 4; }
.line-chart .line {
  fill: none;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 420;
  stroke-dashoffset: 420;
  animation: draw 1.6s .2s cubic-bezier(.4, 0, .2, 1) forwards;
  filter: drop-shadow(0 4px 8px rgba(34, 211, 238, .5));
}
.line-chart .line--2 { stroke: rgba(196, 181, 253, .7); stroke-width: 1.6; stroke-dasharray: 4 5; stroke-dashoffset: 0; animation: fadeIn 1s .6s both; filter: none; }
.line-chart .area { animation: fadeIn 1s 1s both; }
.end-dot { fill: #fff; stroke: #22d3ee; stroke-width: 3; animation: fadeIn .3s 1.7s both, endPulse 2s 2s infinite; transform-origin: 300px 14px; }
@keyframes draw { to { stroke-dashoffset: 0; } }
@keyframes fadeIn { from { opacity: 0; } }
@keyframes endPulse { 50% { r: 6; } }
.bars { position: absolute; inset: 0; display: flex; align-items: flex-end; gap: 10px; padding: 0 4px; }
.bars span {
  flex: 1;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, rgba(255, 255, 255, .28), rgba(255, 255, 255, .04));
  transform-origin: bottom;
  animation: grow .9s cubic-bezier(.2, .8, .2, 1) both;
}
@keyframes grow { from { transform: scaleY(0); } }
.float-chip--a { top: -16px; inset-inline-end: -14px; }
.mini-card--donut { bottom: -20px; inset-inline-start: -22px; }
.donut { width: 30px; height: 30px; transform: rotate(-90deg); }
.donut-bg { fill: none; stroke: rgba(255, 255, 255, .15); stroke-width: 5; }
.donut-val { fill: none; stroke: #a78bfa; stroke-width: 5; stroke-linecap: round; stroke-dasharray: 88; stroke-dashoffset: 88; animation: donut 1.2s 1.2s cubic-bezier(.4, 0, .2, 1) forwards; }
@keyframes donut { to { stroke-dashoffset: 28; } }

/* ---- invoice ---- */
.mock-invoice { display: flex; flex-direction: column; gap: 10px; }
.inv-head { display: flex; align-items: center; gap: 12px; padding-bottom: 12px; border-bottom: 1px dashed rgba(255, 255, 255, .2); }
.inv-ic { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; background: linear-gradient(135deg, #3b82f6, #22d3ee); box-shadow: 0 8px 18px rgba(34, 211, 238, .35); }
.inv-no { font-size: 12px; opacity: .7; letter-spacing: .5px; }
.inv-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .07);
  animation: slideIn .5s cubic-bezier(.2, .8, .2, 1) both;
}
@keyframes slideIn { from { opacity: 0; transform: translateX(-18px); } }
.inv-row .qty { width: 26px; height: 7px; border-radius: 4px; background: rgba(255, 255, 255, .25); }
.inv-row .amt { width: 54px; height: 7px; border-radius: 4px; background: rgba(255, 255, 255, .45); }
.inv-total {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(34, 211, 238, .22), rgba(59, 130, 246, .18));
  border: 1px solid rgba(255, 255, 255, .15);
  animation: msgIn .5s .6s both;
}
.inv-total b { font-size: 17px; letter-spacing: .4px; }
.stamp {
  position: absolute;
  top: 60px;
  inset-inline-end: 22px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 2px solid #34d399;
  border-radius: 8px;
  color: #6ee7b7;
  font-size: 12px;
  font-weight: 800;
  transform: rotate(-10deg);
  animation: stamp .5s 1.1s cubic-bezier(.2, .8, .2, 1.4) both;
}
@keyframes stamp { from { opacity: 0; transform: rotate(-10deg) scale(2.2); } }
.toast {
  position: absolute;
  bottom: -22px;
  inset-inline-start: -20px;
  width: 190px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 34px rgba(2, 8, 40, .45);
  animation: chipIn .6s 1.5s cubic-bezier(.2, .8, .2, 1.3) both, float 3.6s 2.2s ease-in-out infinite;
}
.toast-ic { width: 26px; height: 26px; border-radius: 8px; display: grid; place-items: center; background: #10b981; flex: 0 0 auto; }
.toast .lines i { background: #cbd5e1; }
.toast .lines i.short { background: #e2e8f0; }

/* ---- stock ---- */
.mock-stock { display: flex; flex-direction: column; gap: 10px; }
.stock-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .07);
  animation: slideIn .5s cubic-bezier(.2, .8, .2, 1) both;
}
.stock-ic { width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center; flex: 0 0 auto; }
.stock-body { flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.meter { display: block; height: 6px; border-radius: 4px; background: rgba(255, 255, 255, .12); overflow: hidden; }
.meter span { display: block; height: 100%; border-radius: inherit; transform-origin: left; animation: meter 1s cubic-bezier(.2, .8, .2, 1) both; }
.v-application--is-rtl .meter span { transform-origin: right; }
@keyframes meter { from { transform: scaleX(0); } }
.stock-val { font-size: 12px; min-width: 34px; text-align: end; opacity: .9; }
.float-chip--warn { top: -16px; inset-inline-end: -12px; }
.mini-card--scan { bottom: -20px; inset-inline-start: -18px; width: 64px; height: 50px; justify-content: center; overflow: hidden; }
.scan-line { position: absolute; left: 8px; right: 8px; height: 2px; background: #f43f5e; box-shadow: 0 0 10px #f43f5e; animation: scan 1.6s ease-in-out infinite alternate; }
@keyframes scan { from { top: 8px; } to { top: 40px; } }

/* ---- security ---- */
.mock-shield { height: 260px; display: grid; place-items: center; overflow: hidden; }
.mock-shield .shield {
  width: 92px;
  height: 92px;
  border-radius: 26px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #34d399, #059669);
  box-shadow: 0 0 0 8px rgba(52, 211, 153, .15), 0 22px 44px -10px rgba(5, 150, 105, .8);
  position: relative;
  z-index: 2;
  animation: pop .6s cubic-bezier(.2, .8, .2, 1.3) both;
}
@keyframes pop { from { opacity: 0; transform: scale(.6); } }
.mock-shield .ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(110, 231, 183, .45);
  animation: pulse 3.6s ease-out infinite;
}
.mock-shield .ring--1 { width: 140px; height: 140px; }
.mock-shield .ring--2 { width: 140px; height: 140px; animation-delay: 1.2s; }
.mock-shield .ring--3 { width: 140px; height: 140px; animation-delay: 2.4s; }
@keyframes pulse { from { transform: scale(.7); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }
.orbit {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, .18);
  animation: orbSpin 12s linear infinite;
}
.orbit--2 { width: 150px; height: 150px; animation-duration: 8s; animation-direction: reverse; }
.sat {
  position: absolute;
  top: -14px;
  left: calc(50% - 14px);
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: #3b82f6;
  box-shadow: 0 6px 14px rgba(59, 130, 246, .6);
}
.sat--2 { background: #8b5cf6; box-shadow: 0 6px 14px rgba(139, 92, 246, .6); }

/* copy */
.slide-copy { min-height: 210px; display: flex; flex-direction: column; }
.slide-kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(255, 255, 255, .12);
  border: 1px solid rgba(255, 255, 255, .2);
  margin-bottom: 16px;
}
.brand-title { font-size: 30px; font-weight: 800; line-height: 1.5; margin: 0 0 10px; }
.brand-title--ai {
  background: linear-gradient(90deg, #fff 0%, #c4b5fd 50%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.brand-subtitle { font-size: 15px; line-height: 1.95; opacity: .8; margin: 0; max-width: 480px; }
.copy-enter-active, .copy-leave-active { transition: opacity .35s ease, transform .35s ease; }
.copy-enter { opacity: 0; transform: translateY(12px); }
.copy-leave-to { opacity: 0; transform: translateY(-8px); }

.slide-nav { display: flex; gap: 8px; margin-top: auto; padding-top: 24px; }
.dot {
  width: 10px;
  height: 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, .28);
  overflow: hidden;
  position: relative;
  transition: width .35s ease;
  cursor: pointer;
}
.dot--active { width: 44px; }
.dot-fill {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #67e8f9, #fff);
  border-radius: inherit;
  transform-origin: left;
  animation: fill linear both;
}
[dir='rtl'] .dot-fill, .v-application--is-rtl .dot-fill { transform-origin: right; }
.dot--paused .dot-fill { animation-play-state: paused; }
@keyframes fill { from { transform: scaleX(0); } to { transform: scaleX(1); } }

@media (prefers-reduced-motion: reduce) {
  .auth-brand *, .auth-brand *::before, .auth-brand *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; }
}

@media (max-width: 1200px) {
  .brand-inner { padding: 36px; }
  .brand-title { font-size: 25px; }
  .auth-topbar, .auth-body { padding-left: 28px; padding-right: 28px; }
}

/* ================= mobile ================= */
@media (max-width: 959px) {
  .auth-brand { display: none; }
  .auth-main {
    background: radial-gradient(90% 40% at 0% 0%, rgba(59, 130, 246, .6), transparent 70%), radial-gradient(80% 40% at 100% 20%, rgba(124, 58, 237, .45), transparent 70%), linear-gradient(160deg, #0a3bd6 0%, #0b2577 60%, #0a1440 100%);
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
  .auth-topbar { padding: 10px 16px 0; }
  .auth-logo { height: 22px; filter: brightness(0) invert(1); }
  .auth-hero {
    display: block;
    padding: 8px 16px 14px;
    color: #fff;
    min-height: 0;
  }
  .hero-text { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; opacity: .95; }
  .hero-fade-enter-active, .hero-fade-leave-active { transition: opacity .3s, transform .3s; }
  .hero-fade-enter, .hero-fade-leave-to { opacity: 0; transform: translateY(6px); }
  .auth-body {
    flex: 1;
    min-height: 0;
    align-items: stretch;
    padding: 0;
    overflow-y: auto;
  }
  .auth-card {
    max-width: none;
    background: #fff;
    border-radius: 22px 22px 0 0;
    padding: 16px 16px 4px;
    box-shadow: 0 -10px 30px rgba(3, 15, 50, .18);
  }
  .auth-main ::v-deep .app-footer {
    background: #fff;
    padding: 6px 12px 10px;
    font-size: 10px;
  }
}
</style>
